<template>
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
    <div
      class="flex items-center justify-between p-2.5 bg-white dark:bg-gray-800 rounded-lg border-2 border-emerald-500 dark:border-emerald-600"
    >
      <div class="flex items-center gap-3">
        <svg
          class="w-5 h-5 text-emerald-600 dark:text-emerald-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <span class="text-sm font-medium text-gray-900 dark:text-white">
          {{ t('app.subscription.settings.payment_methods.cash') }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Siempre activo</span>
        <svg
          class="w-5 h-5 text-emerald-600 dark:text-emerald-400"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
    </div>

    <div
      v-for="method in paymentMethodItems"
      :key="method.key"
      class="flex items-center justify-between p-2.5 bg-white dark:bg-gray-800 rounded-lg border-2 transition-colors"
      :class="isEnabled(method.key) ? 'border-indigo-500 dark:border-indigo-600' : 'border-gray-200 dark:border-gray-700'"
    >
      <div class="flex items-center gap-3">
        <svg
          class="w-5 h-5"
          :class="isEnabled(method.key) ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            :d="method.iconPath"
          />
        </svg>
        <span class="text-sm font-medium text-gray-900 dark:text-white">
          {{ t(method.labelKey) }}
        </span>
      </div>
      <button
        :disabled="isSaving"
        :class="[
          'relative inline-flex h-5 w-9 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 dark:focus:ring-offset-gray-800',
          isEnabled(method.key) ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600',
          isSaving ? 'opacity-50 cursor-not-allowed' : ''
        ]"
        @click="emit('toggle', method.key)"
      >
        <span
          :class="[
            'pointer-events-none inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
            isEnabled(method.key) ? 'translate-x-4 sm:translate-x-5' : 'translate-x-0'
          ]"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { PaymentMethodsConfig } from '@/composables/useRestaurantSettings'

type PaymentMethodKey = 'card' | 'digital' | 'other'

interface PaymentMethodItem {
  key: PaymentMethodKey
  labelKey: string
  iconPath: string
}

const props = defineProps<{
  isSaving: boolean
  paymentMethodsConfig: PaymentMethodsConfig
  paymentMethodItems: PaymentMethodItem[]
}>()

const emit = defineEmits<{
  (e: 'toggle', method: PaymentMethodKey): void
}>()

const { t } = useI18n()

const isEnabled = (key: PaymentMethodKey): boolean => Boolean(props.paymentMethodsConfig?.[key])
</script>
